import { DropZone } from '@client/new/components/DropZone/DropZone';
import { Typography, Card, Box, CircularProgress } from '@mui/material';
import { allowedVideoTypes } from '../videos/videos.constants';
import { useExerciseUpload } from './useExerciseUpload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faTimes, faVideo } from '@fortawesome/pro-regular-svg-icons';
import { PlainModal } from '@client/new/components/Modal/Modal';
import { CustomPlayer } from '../videos/components/ClipsGallery';
import { LibraryVideo } from '../videos/videos.domain';
import { Media } from 'generated/graphql';
import { MediaUploadModal } from './MediaUploadModal';
import { useState } from 'react';
import toast from 'react-hot-toast';
import uuid4 from 'uuid4';

export const DropeZoneModal = (p: {
  onChange: (file: File) => void;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const onError = (message: string) => toast.error(message);

  return (
    <PlainModal
      isOpen={p.isOpen}
      onClose={p.onToggle}
      title="Upload your video"
      height={'auto'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        padding: '24px',
        maxWidth: '500px',
        minHeight: '300px',
      }}
    >
      <div className="flex justify-between">
        <Typography variant="h4">Upload your video</Typography>
        <div className="cursor-pointer" onClick={p.onToggle} role="button">
          <FontAwesomeIcon
            icon={faTimes}
            className="ml-3 leading-none text-sm w-[28px] h-[28px] self-center"
          />
        </div>
      </div>

      <DropZone
        accept={allowedVideoTypes}
        onChange={p.onChange}
        infoText="Select .mp4 or .mov file up to 90s long"
        onlySingleFile={true}
        onError={onError}
      />
    </PlainModal>
  );
};

const VideoPreviewState = ({
  uploadedFile,
  toggleDropzone,
  progress,
  isUploadingMedia,
  removeMedia,
}: {
  uploadedFile: LibraryVideo | null;
  toggleDropzone: () => void;
  progress: number;
  isUploadingMedia: boolean;
  removeMedia: () => void;
}) => {
  if (!uploadedFile && isUploadingMedia) {
    return (
      <Card className="w-[150px] h-[266.6px] bg-gray-100 flex items-center justify-center">
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="caption"
              component="div"
              color="text.secondary"
            >
              <div className="text-center">
                <p>Processing...</p>
                <CircularProgress />
              </div>
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  }
  if (uploadedFile) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Card className="aspect-[9/16] w-[150px] h-[266.6px] bg-gray-100 flex items-center justify-center cursor-pointer">
          <CustomPlayer
            src={uploadedFile.videoSrc}
            poster={uploadedFile.poster}
            height="100%"
            width="100%"
            className="h-full w-full"
          />
          <Box
            sx={{
              position: 'absolute',
              backgroundColor: 'white',
              top: '-8px',
              right: '-5px',
              cursor: 'pointer',
              borderRadius: '14px',
              border: '1px solid #EAECF1',
              width: '28px',
              height: '28px',
            }}
            onClick={removeMedia}
          >
            <FontAwesomeIcon
              icon={faClose}
              color="#151E33"
              className="w-full h-full opacity-50 hover:text-red-500 hover:opacity-100"
            />
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Card className="aspect-[9/16] w-[150px] h-[266.6px] bg-gray-100 flex items-center justify-center cursor-pointer relative">
      <Box
        className="flex flex-col justify-center items-center w-[150px] h-[266.6px]"
        onClick={toggleDropzone}
      >
        <FontAwesomeIcon
          icon={faVideo}
          color="#8A8E99"
          className="w-[44px] h-[50px]"
        />
        <div className="text-primary">Video</div>
        <div className="text-sm opacity-50 text-[#151E33]">Vertical (9:16)</div>
        <div className="text-sm opacity-50 text-[#151E33]">100MB max</div>
      </Box>
    </Card>
  );
};

export const ExerciseUpload = ({
  influencerId,
  video,
  onChange,
  setUploadingMedia,
}: {
  influencerId: number;
  onChange: (media: Media | null) => void;
  setUploadingMedia: ({
    isUploading,
    id,
  }: {
    isUploading: boolean;
    id: string;
  }) => void;
  video?: Media;
}) => {
  const handleIsProcessingChange = (isUploading: boolean) => {
    setUploadingMedia({ isUploading, id });
    setIsProcessing(isUploading);
  };

  const [isProcessing, setIsProcessing] = useState(false);
  const [id] = useState<string>(uuid4());

  const {
    showDropZone,
    showEditMedia,
    media,
    step,
    editParams,
    uploadedFile,
    progress,
    toggleDropzone,
    selectVideo,
    cancelEdit,
    upload,
    removeMedia,
    setThumbnail,
    onValidationSuccess,
  } = useExerciseUpload({
    influencerId,
    video,
    onChange,
    setUploadingMedia: handleIsProcessingChange,
  });
  return (
    <>
      <VideoPreviewState
        toggleDropzone={toggleDropzone}
        progress={progress}
        uploadedFile={uploadedFile}
        isUploadingMedia={isProcessing}
        removeMedia={removeMedia}
      />
      {showDropZone && (
        <DropeZoneModal
          onChange={selectVideo}
          onToggle={toggleDropzone}
          isOpen={showDropZone}
        />
      )}
      {showEditMedia && media && (
        <MediaUploadModal
          editParams={editParams}
          media={media}
          isOpen={showEditMedia}
          onClose={cancelEdit}
          onCancel={cancelEdit}
          onUpload={upload}
          onSaveThumbnail={setThumbnail}
          step={step}
          selectVideo={selectVideo}
          onValidationSuccess={onValidationSuccess}
        />
      )}
    </>
  );
};
